/**
 * Upload-mutation surface for `POST /invoices` (#44).
 *
 * - `uploadInvoiceFile`     transport-level fetch (multipart, schema parse).
 * - `runWithConcurrency`    classic worker-pool used by the dialog to cap
 *                            parallel uploads at 3 (per the architect spec
 *                            so we never DoS the backend on big batches).
 * - Validation constants    mirror the server caps in
 *                            `apps/api/src/lib/upload-validation.ts` (#27)
 *                            so the UI rejects obvious failures BEFORE
 *                            paying for a network round-trip.
 *
 * Per-file UI state lives inside the dialog (`PerFileState`) - this module
 * stays free of React.
 */
import { type InvoiceDocumentType, UploadResponseSchema } from '@invoice/shared/schemas';
import type { z } from 'zod';
import { ApiError, apiFetch } from '../api';

export type UploadResponse = z.infer<typeof UploadResponseSchema>;

/** Mirror of the server-side cap (`MAX_BYTES` in `upload-validation.ts`, #27). */
export const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

/**
 * Mime allow-list. The server runs a magic-byte sniff (#27) on top of this,
 * but rejecting on extension+mime client-side gives the user instant
 * feedback for obvious mistakes (drag a `.jpg`, etc.).
 */
export const ACCEPTED_MIME_TYPES = ['application/pdf', 'application/xml', 'text/xml'] as const;

export const ACCEPTED_EXTENSIONS = ['.pdf', '.xml'] as const;

/**
 * Concurrency limit per the architect spec (#44). Keeping this in the
 * shared module makes it trivial to test the pool against the same
 * constant the dialog uses.
 */
export const UPLOAD_CONCURRENCY = 3;

/** Subset of the validation result the dialog renders inline. */
export interface UploadValidationResult {
  ok: boolean;
  /** German-locale message; rendered next to the file row. */
  message?: string;
  /** Stable code so tests / future i18n can branch on the reason. */
  code?: 'invalid_type' | 'too_large';
}

/**
 * Client-side pre-flight validation. The server still runs the real
 * validation (#27); this only catches the obvious cases.
 */
export function validateUploadFile(file: File): UploadValidationResult {
  const lowerName = file.name.toLowerCase();
  const extOk = ACCEPTED_EXTENSIONS.some((ext) => lowerName.endsWith(ext));
  const mimeOk =
    file.type.length === 0 || (ACCEPTED_MIME_TYPES as readonly string[]).includes(file.type);

  if (!extOk || !mimeOk) {
    return {
      ok: false,
      code: 'invalid_type',
      message: `Nur PDF- oder XML-Dateien sind erlaubt (${file.name})`,
    };
  }
  if (file.size > MAX_UPLOAD_BYTES) {
    return {
      ok: false,
      code: 'too_large',
      message: `Datei ist größer als 20 MB (${file.name})`,
    };
  }
  return { ok: true };
}

interface UploadInput {
  file: File;
  tenantId?: string;
  documentType?: InvoiceDocumentType;
  signal?: AbortSignal;
}

/**
 * Send a single file to `POST /invoices`. The body is `multipart/form-data`
 * with `file` and, when explicitly chosen, `tenantId` per the upload
 * contract (#26 / #44).
 */
export async function uploadInvoiceFile(input: UploadInput): Promise<UploadResponse> {
  const fd = new FormData();
  fd.append('file', input.file, input.file.name);
  if (input.tenantId && input.tenantId.length > 0) {
    fd.append('tenantId', input.tenantId);
  }
  if (input.documentType) {
    fd.append('documentType', input.documentType);
  }

  return apiFetch('/invoices', {
    method: 'POST',
    body: fd,
    schema: UploadResponseSchema,
    ...(input.signal ? { signal: input.signal } : {}),
  });
}

/**
 * Worker-pool: run `worker(item)` for each input with at most `limit`
 * promises in flight. Returns once every worker has resolved or rejected;
 * individual rejections do not cancel the rest of the queue (so a single
 * file failure does not block the rest of the batch).
 *
 * The implementation is recursive but stack-safe: each worker pulls the
 * next index from a shared cursor, so we never build an array of `limit *
 * items.length` pending continuations.
 */
export async function runWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  worker: (item: T, index: number) => Promise<R>,
): Promise<PromiseSettledResult<R>[]> {
  const results: PromiseSettledResult<R>[] = new Array(items.length);
  const effectiveLimit = Math.max(1, Math.min(limit, items.length));
  let cursor = 0;

  async function runner(): Promise<void> {
    while (true) {
      const idx = cursor;
      cursor += 1;
      if (idx >= items.length) return;
      const item = items[idx];
      if (item === undefined) return;
      try {
        const value = await worker(item, idx);
        results[idx] = { status: 'fulfilled', value };
      } catch (err) {
        results[idx] = { status: 'rejected', reason: err };
      }
    }
  }

  await Promise.all(Array.from({ length: effectiveLimit }, () => runner()));
  return results;
}

/**
 * Unwrap an unknown rejection thrown by `uploadInvoiceFile` into the
 * German-locale string the toast/file-row renders. We surface the API
 * envelope's `message` when available, otherwise a generic fallback.
 */
export function describeUploadError(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unbekannter Fehler beim Upload';
}
